import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  TableProperties,
  LineChart,
  Settings,
  Layers,
  Calculator,
  Activity,
  Users,
  ArrowLeftRight,
  Sparkles,
  Scale,
  PiggyBank,
  TrendingUp,
  GitBranch,
  ChevronDown,
  CandlestickChart,
  ScrollText,
  KanbanSquare,
  Workflow,
  Waves,
  Rocket,
  Sigma,
  Wallet,
  FolderOpen,
  Wrench,
  BookOpen,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { accountsApi } from '@/services/api';
import { NIGHTINGALE_5_TABS } from '@/config/nightingale5';

import { Button } from '@/components/ui/button';
interface NavigationItem {
  name: string;
  path: string;
  icon: React.ElementType;
  badge?: string;
}

const navigationTop: NavigationItem[] = [
  { name: 'Dashboard', path: '/', icon: Home },
];

// Analysis tools — grouped into one collapsible section between Dashboard and Nightingale.
const toolItems: NavigationItem[] = [
  { name: 'Options Chain', path: '/options-chain', icon: TableProperties },
  { name: 'Volatility', path: '/volatility', icon: LineChart },
  { name: 'Skew Analysis', path: '/skew', icon: Waves, badge: 'NEW' },
  { name: 'Candle Charts', path: '/charts', icon: CandlestickChart },
  { name: 'Strategy Builder', path: '/strategy-builder', icon: Layers },
  { name: 'Index Returns', path: '/index-returns', icon: GitBranch },
  { name: 'Crawler Calculator', path: '/crawler-calculator', icon: Calculator },
];

const navigationNightingale: NavigationItem[] = [
  { name: 'Nightingale', path: '/nightingale', icon: Sparkles },
];

const nightingale5Items: NavigationItem[] = NIGHTINGALE_5_TABS.map((t) => ({
  name: t.label,
  path: `/nightingale-5/${t.slug}`,
  icon: t.icon,
}));

// Nightingale Sub Algos — order is fixed per trader's spec.
const nightingaleSubAlgos: NavigationItem[] = [
  { name: 'Trade Initiation', path: '/trade-initiation', icon: Rocket },
  { name: 'Lambda Signals', path: '/lambda-algo', icon: Sigma, badge: 'NEW' },
  // Trade Initiation v2 (2026-09-21) and the compare page (2026-09-26) are archived — owner 2026-09-27,
  // "right now i don't need it". Pages and backend kept: frontend/src/pages/archive/README.md restores them.
  // Leverage Buy Climb (/leverage-buys) and the crawler-only Lambda + PS Algo (/crawler-algo) are hidden
  // from the menu — owner 2026-10-14: show only the merged page. Their routes still work by URL.
  { name: 'Lambda + PS Algo', path: '/climb-algo', icon: Activity },
  { name: 'Maximisation', path: '/maximisation', icon: TrendingUp },
  // Range-bound trades: pairs 2 and 3 sold together, then Maximisation (owner 2026-10-22)
  { name: 'Pair 2 + 3', path: '/pair-2-3', icon: Layers, badge: 'NEW' },
  { name: 'Cluster Action', path: '/cluster-action', icon: PiggyBank },
  { name: 'Least Action', path: '/least-action', icon: Scale },
  { name: 'Expiry Transfer', path: '/expiry-transfer', icon: ArrowLeftRight },
  { name: 'Last Action', path: '/last-action', icon: Scale, badge: 'TEST' },
];

const navigationBottom: NavigationItem[] = [
  // Every page's guide in one place — how it works, every number, release notes (owner 2026-10-27)
  { name: 'Guides', path: '/guides', icon: BookOpen, badge: 'NEW' },
  // The work board — not a strategy, so it sits below the trading group.
  { name: 'Tasks', path: '/tasks', icon: KanbanSquare, badge: 'NEW' },
  // The team runbook: how to work on live trades locally without touching
  // the engines on the instance. Hidden from viewers like the board is.
  { name: 'Dev Workflow', path: '/dev-workflow', icon: Workflow, badge: 'NEW' },
];

interface SidebarProps {
  isOpen: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const { user } = useAuthStore();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Managers see everything except the Nightingale main page (hidden from nav
  // and blocked at the route level in App.tsx).
  const visibleNightingale = user?.role === 'manager'
    ? navigationNightingale.filter((item) => item.path !== '/nightingale')
    : navigationNightingale;

  // Every task route is 403 for a viewer — the board is a thing you work, not a
  // thing you watch — so the entry is hidden rather than left to lead at a wall.
  const visibleBottom = user?.role === 'viewer'
    ? navigationBottom.filter((item) => item.path !== '/tasks' && item.path !== '/dev-workflow')
    : navigationBottom;

  // Server decides — the allowlist of partner emails lives in backend config.
  const [accountsAllowed, setAccountsAllowed] = useState(false);
  useEffect(() => {
    if (!user) { setAccountsAllowed(false); return; }
    accountsApi.access()
      .then((a) => setAccountsAllowed(a.allowed))
      .catch(() => setAccountsAllowed(false));
  }, [user?.id]);

  const subAlgosActive = nightingaleSubAlgos.some((i) => isActive(i.path));
  const [subAlgosOpen, setSubAlgosOpen] = useState<boolean>(subAlgosActive);

  const toolsActive = toolItems.some((i) => isActive(i.path));
  const [toolsOpen, setToolsOpen] = useState<boolean>(toolsActive);

  const nightingale5Active = nightingale5Items.some((i) => isActive(i.path));
  const [nightingale5Open, setNightingale5Open] = useState<boolean>(nightingale5Active);

  // Navigating into a grouped page (e.g. via a link) auto-expands its group.
  useEffect(() => {
    if (subAlgosActive) setSubAlgosOpen(true);
    if (nightingale5Active) setNightingale5Open(true);
    if (toolsActive) setToolsOpen(true);
  }, [location.pathname]);

  const renderItem = (item: NavigationItem, indent = false) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        title={!isOpen ? item.name : undefined}
        className={`
          flex items-center ${isOpen ? `space-x-3 ${indent ? 'pl-6 pr-3' : 'px-3'}` : 'justify-center px-0'} py-2.5 rounded-lg
          text-body font-medium transition-colors
          ${active
            ? 'bg-primary-500/10 text-muted-foreground border border-primary-500/20'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          }
        `}
      >
        <Icon size={indent && isOpen ? 18 : 20} className={active ? 'text-muted-foreground' : ''} />
        {isOpen && (
          <span className="flex items-center gap-2 whitespace-nowrap">
            {item.name}
            {item.badge && (
              <span className="px-1.5 py-0.5 rounded border border-success-500/30 bg-success-500/20 text-success-400 text-eyebrow font-semibold uppercase tracking-widest">
                {item.badge}
              </span>
            )}
          </span>
        )}
      </Link>
    );
  };

  const renderGroup = (
    label: string,
    GroupIcon: React.ElementType,
    items: NavigationItem[],
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    // Collapsed sidebar: grouped pages stay reachable as icon links.
    if (!isOpen) return items.map((item) => renderItem(item));
    const groupActive = items.some((i) => isActive(i.path));
    return (
      <>
        <Button variant="ghost" size="inline"
          onClick={() => setOpen((o) => !o)}
          className={`
            w-full flex items-center justify-between px-3 py-2.5 rounded-lg
            text-body font-medium normal-case tracking-normal transition-colors
            ${groupActive && !open
              ? 'text-muted-foreground hover:bg-accent'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }
          `}
        >
          <span className="flex min-w-0 items-center space-x-3">
            <GroupIcon size={20} className="shrink-0" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </Button>
        {open && items.map((item) => renderItem(item, true))}
      </>
    );
  };

  return (
    <div
      className={`flex flex-col bg-background border-r border-border h-screen sticky top-0 transition-all duration-300 ${isOpen ? 'w-64' : 'w-16'
        }`}
    >
      {/* Logo */}
      <div className="flex items-center justify-center h-16 border-b border-border overflow-hidden">
        {isOpen ? (
          <div className="flex items-center gap-2.5 whitespace-nowrap">
            <img src="/logo-mark.png" alt="Kavas Capital-Nightingale" className="h-[22px] w-auto invert dark:invert-0" />
            <h1 className="text-title font-bold tracking-tighter">
              <span className="text-muted-foreground">Kavas Capital-</span><span className="text-foreground">Nightingale</span>
            </h1>
          </div>
        ) : (
          <img src="/logo-mark.png" alt="Kavas Capital-Nightingale" className="h-[22px] w-auto invert dark:invert-0" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4">
        <div className={`space-y-1 ${isOpen ? 'px-3' : 'px-2'}`}>
          {navigationTop.map((item) => renderItem(item))}

          {renderGroup('Tools', Wrench, toolItems, toolsOpen, setToolsOpen)}

          {visibleNightingale.map((item) => renderItem(item))}

          {renderGroup('Nightingale 5.0', Sparkles, nightingale5Items, nightingale5Open, setNightingale5Open)}

          {renderGroup('Nightingale Sub Algos', GitBranch, nightingaleSubAlgos, subAlgosOpen, setSubAlgosOpen)}

          {visibleBottom.map((item) => renderItem(item))}

          {/* Admin Section */}
          {user?.role === 'super_admin' && (
            <>
              <Link
                to="/users"
                title={!isOpen ? 'Team Management' : undefined}
                className={`
                  flex items-center ${isOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2.5 rounded-lg
                  text-body font-medium transition-colors
                  ${isActive('/users')
                    ? 'bg-primary-500/10 text-muted-foreground border border-primary-500/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }
                `}
              >
                <Users size={20} className={isActive('/users') ? 'text-muted-foreground' : ''} />
                {isOpen && <span className="whitespace-nowrap">Team Management</span>}
              </Link>
              <Link
                to="/activity-log"
                title={!isOpen ? 'Activity Log' : undefined}
                className={`
                  flex items-center ${isOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2.5 rounded-lg
                  text-body font-medium transition-colors
                  ${isActive('/activity-log')
                    ? 'bg-primary-500/10 text-muted-foreground border border-primary-500/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }
                `}
              >
                <ScrollText size={20} className={isActive('/activity-log') ? 'text-muted-foreground' : ''} />
                {isOpen && <span className="whitespace-nowrap">Activity Log</span>}
              </Link>
            </>
          )}

          {/* Account Management — the two partners' logins only (by email, not role) */}
          {accountsAllowed && (
            <Link
              to="/accounts"
              title={!isOpen ? 'Account Management' : undefined}
              className={`
                flex items-center ${isOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2.5 rounded-lg
                text-body font-medium transition-colors
                ${isActive('/accounts')
                  ? 'bg-primary-500/10 text-muted-foreground border border-primary-500/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }
              `}
            >
              <Wallet size={20} className={isActive('/accounts') ? 'text-muted-foreground' : ''} />
              {isOpen && <span className="whitespace-nowrap">Account Management</span>}
            </Link>
          )}

          {/* Documents cabinet — same partners-only gate as Account Management */}
          {accountsAllowed && (
            <Link
              to="/documents"
              title={!isOpen ? 'Documents' : undefined}
              className={`
                flex items-center ${isOpen ? 'space-x-3 px-3' : 'justify-center px-0'} py-2.5 rounded-lg
                text-body font-medium transition-colors
                ${isActive('/documents')
                  ? 'bg-primary-500/10 text-muted-foreground border border-primary-500/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }
              `}
            >
              <FolderOpen size={20} className={isActive('/documents') ? 'text-muted-foreground' : ''} />
              {isOpen && <span className="whitespace-nowrap">Documents</span>}
            </Link>
          )}
        </div>
      </nav>

      {/* Footer */}
      <div className={`border-t border-dark-700 ${isOpen ? 'p-4' : 'p-2'}`}>
        <Link
          to="/settings"
          title={!isOpen ? 'Settings' : undefined}
          className={`flex items-center ${isOpen ? 'space-x-3 px-3' : 'justify-center px-0'
            } py-2.5 rounded-lg text-body font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors`}
        >
          <Settings size={20} />
          {isOpen && <span>Settings</span>}
        </Link>
      </div>
    </div>
  );
};
